import { useState, useEffect } from 'react';
import { CreateUserProps, UserFieldErrors, UserTouched, UserListingProps } from '../../utils/interface/userListing';
import { validateUserForm, extractApiError } from '../../utils/util';
import { createUser, updateUser } from '../../api/users/user';
import toast from "react-hot-toast";
// import { signUp } from '@aws-amplify/auth';
import { uploadThumbnail } from '../../api/upload/file';
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../utils/queryKeys";
interface ExtendedCreateUserProps extends CreateUserProps {
  initialData?: UserListingProps['userList'][0];
  isEditMode?: boolean;
  refershUsers: () => void;
  setIsModalOpen: (open: boolean) => void;
  /** Optional label for submit button (e.g. "SUBMIT" or "UPDATE" for page layout) */
  submitButtonText?: string;
  profileImage?: string | null;
  profileImageFile?: File | null;
}

const CreateUser = ({ initialData, isEditMode = false, refershUsers, setIsModalOpen, submitButtonText, profileImage, profileImageFile }: ExtendedCreateUserProps) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    firstName: initialData?.fullName || '',
    lastName: initialData?.lastName || '',
    email: initialData?.email || '',
    companyName: initialData?.companyName ?? '',
    jobTitle: initialData?.jobTitle ?? '',
    phone: initialData?.phone ?? ''
  });
  const [touched, setTouched] = useState<UserTouched>({});
  const [fieldErrors, setFieldErrors] = useState<UserFieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  useEffect(() => {
    if (initialData && isEditMode) {
      setFormData({
        // Use empty string (not a space) so placeholders remain visible when missing.
        firstName: initialData.fullName ?? '',
        lastName: initialData.lastName ?? '',
        email: initialData.email || '',
        companyName: initialData.companyName ?? '',
        jobTitle: initialData.jobTitle ?? '',
        phone: initialData.phone ?? ''
      });
    }
  }, [initialData, isEditMode]);

  const saveUserMutation = useMutation({
    mutationFn: async () => {
      let imageUrl = '';
      if (profileImageFile) {
        imageUrl = await uploadThumbnail(profileImageFile, "users");
      }

      if (!isEditMode) {
        const payload = {
          ...formData,
          profileImage: imageUrl ? imageUrl : '',
        };
        await createUser(payload);
        return;
      }

      const updateUserDetails = {
        fullName: formData.firstName,
        companyName: formData.companyName || undefined,
        jobTitle: formData.jobTitle || undefined,
        phone: formData.phone || undefined,
        ...(imageUrl && { profileImage: imageUrl }),
      };
      await updateUser(initialData?.id || '', { ...updateUserDetails });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.users.all() });
    },
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const nextValue =
      name === "phone"
        ? value.replace(/\D/g, "")
        : name === "email"
          ? value.toLowerCase()
          : value;
    setFormData({
      ...formData,
      [name]: nextValue
    });

    // Mark field as touched
    setTouched({
      ...touched,
      [name]: true
    });

    // Validate field on change
    const newFormData = { ...formData, [name]: nextValue };
    const errors = validateUserForm(newFormData);
    setFieldErrors(errors);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name } = e.target;
    setTouched({
      ...touched,
      [name]: true
    });

    // Validate field on blur
    const errors = validateUserForm(formData);
    setFieldErrors(errors);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    const errors = validateUserForm(formData);
    setFieldErrors(errors);

    setTouched({
      firstName: true,
      lastName: true,
      email: true,
      companyName: true,
      jobTitle: true,
      phone: true
    });
    if (Object.keys(errors).length !== 0) return;

    try {
      await saveUserMutation.mutateAsync();

      if (!isEditMode) {
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          companyName: '',
          jobTitle: '',
          phone: ''
        });
        setTouched({});
        setFieldErrors({});
      }

      toast.success(isEditMode ? "User updated" : "User created");
      refershUsers();
      setIsModalOpen(false);
    } catch (error: unknown) {
      const message = extractApiError(error);
      setSubmitError(message);
      toast.error(message);
    } finally {
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 add-user-form">
      {submitError && (
        <div className="p-4 rounded-[27px] border border-red-500/50 bg-red-500/10 text-red-400 text-sm flex items-center gap-2">
          <svg className="w-5 h-5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {submitError}
        </div>
      )}
      <div>
        <label className="block text-base text-white mb-4 font-light">
          Full Name
        </label>
        <input
          type="text"
          name="firstName"
          value={formData.firstName}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="Enter full name"
          className={`w-full h-[53px] px-4 border rounded-[27px] text-white focus:ring-2 focus:ring-[#E761A0]/50 focus:outline-none transition-all duration-200 placeholder-gray-500 ${touched.firstName && fieldErrors.firstName
            ? "border-red-500/50 bg-transparent"
            : "border-[#E761A0] bg-transparent hover:border-[#E761A0]"
            }`}
        />
        {touched.firstName && fieldErrors.firstName && (
          <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            {fieldErrors.firstName}
          </p>
        )}
      </div>
      <div>
        <label className="block text-base text-white mb-4 font-light">
          Company
        </label>
        <input
          type="text"
          name="companyName"
          value={formData.companyName ?? ""}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="Enter company name"
          className={`w-full h-[53px] px-4 border rounded-[27px] text-white focus:ring-2 focus:ring-[#E761A0]/50 focus:outline-none transition-all duration-200 placeholder-gray-500 ${touched.companyName && fieldErrors.companyName
            ? "border-red-500/50 bg-transparent"
            : "border-[#E761A0] bg-transparent hover:border-[#E761A0]"
            }`}
        />
        {touched.companyName && fieldErrors.companyName && (
          <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            {fieldErrors.companyName}
          </p>
        )}
      </div>

      <div>
        <label className="block text-base text-white mb-4 font-light">
          Job Title
        </label>
        <input
          type="text"
          name="jobTitle"
          value={formData.jobTitle ?? ""}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="Enter job title"
          className={`w-full h-[53px] px-4 border rounded-[27px] text-white focus:ring-2 focus:ring-[#E761A0]/50 focus:outline-none transition-all duration-200 placeholder-gray-500 ${touched.jobTitle && fieldErrors.jobTitle
            ? "border-red-500/50 bg-transparent"
            : "border-[#E761A0] bg-transparent hover:border-[#E761A0]"
            }`}
        />
        {touched.jobTitle && fieldErrors.jobTitle && (
          <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            {fieldErrors.jobTitle}
          </p>
        )}
      </div>

      <div>
        <label className="block text-base text-white mb-4 font-light">
          Email <span className="text-red-500">*</span>
        </label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="email@company.com"
          readOnly={isEditMode}
          className={`w-full h-[53px] px-4 border rounded-[27px] text-white focus:ring-2 focus:ring-[#E761A0]/50 focus:outline-none transition-all duration-200 placeholder-gray-500 ${isEditMode ? "cursor-not-allowed opacity-80" : ""
            } ${touched.email && fieldErrors.email
              ? "border-red-500/50 bg-transparent"
              : "border-[#E761A0] bg-transparent hover:border-[#E761A0]"
            }`}
        />
        {touched.email && fieldErrors.email && (
          <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            {fieldErrors.email}
          </p>
        )}
      </div>

      <div>
        <label className="block text-base text-white mb-4 font-light">
          Phone
        </label>
        <input
          type="tel"
          name="phone"
          value={formData.phone ?? ""}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="Enter phone number (3–15 digits)"
          maxLength={15}
          className={`w-full h-[53px] px-4 border rounded-[27px] text-white focus:ring-2 focus:ring-[#E761A0]/50 focus:outline-none transition-all duration-200 placeholder-gray-500 ${touched.phone && fieldErrors.phone
            ? "border-red-500/50 bg-transparent"
            : "border-[#E761A0] bg-transparent hover:border-[#E761A0]"
            }`}
        />
        {touched.phone && fieldErrors.phone && (
          <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            {fieldErrors.phone}
          </p>
        )}
      </div>
      <div className="flex  pt-[30px]">

        <button
          type="submit"
          disabled={saveUserMutation.isPending}
          className="theme-btn min-w-[178px] xl:min-h-[54px] min-h-[45px] text-[12px] xl:text-[16px]"
        >
          {saveUserMutation.isPending && (
            <div className="w-5 h-5 border-2 border-t-transparent border-white rounded-full animate-spin"></div>
          )}
          <span>
            {saveUserMutation.isPending
              ? isEditMode
                ? "Updating..."
                : "Creating..."
              : (submitButtonText ?? (isEditMode ? "Update" : "Create"))}
          </span>
        </button>
      </div>
    </form>
  );
}

export default CreateUser;
